import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  MapPin, 
  Coins, 
  Mail, 
  Pencil, 
  Trash2, 
  Users, 
  ChevronRight 
} from "lucide-react";
import { cn } from "@/lib/utils";

interface EmployeeCardProps {
  employee: {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    position?: string;
    department?: string;
    manager_id?: string | null;
    invitation_status: string;
    accepted_at: string | null;
    current_country?: string;
    residence_country?: string;
    employee_points?: Array<{
      total_points: number;
      used_points: number;
    }>;
  };
  displayName: string;
  displayPosition: string;
  displayDepartment: string | null;
  availablePoints: number;
  maxPoints: number;
  managerName: string | null;
  teamMembersCount: number;
  onEdit: () => void;
  onDelete: () => void;
  onSendInvitation?: () => void;
  onShowHierarchy: () => void;
  getStatusBadge: (status: string, acceptedAt: string | null) => React.ReactNode;
}

export const EmployeeCard = ({
  employee,
  displayName,
  displayPosition,
  displayDepartment,
  availablePoints,
  maxPoints,
  managerName,
  teamMembersCount,
  onEdit,
  onDelete,
  onSendInvitation,
  onShowHierarchy,
  getStatusBadge,
}: EmployeeCardProps) => {
  const initials = employee.first_name && employee.last_name
    ? `${employee.first_name[0]}${employee.last_name[0]}`
    : "?";

  return (
    <div className="p-4 border rounded-lg bg-card space-y-3">
      {/* Header with avatar, name, and status */}
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button 
              onClick={onShowHierarchy}
              className="font-medium text-left hover:text-primary transition-colors flex items-center gap-1"
            >
              {displayName}
              {(teamMembersCount > 0 || employee.manager_id) && (
                <Users className="h-3.5 w-3.5 text-muted-foreground" />
              )}
            </button>
            {teamMembersCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {teamMembersCount} membre{teamMembersCount > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {employee.email}
          </p>
        </div>
        <div className="shrink-0">
          {getStatusBadge(employee.invitation_status, employee.accepted_at)}
        </div>
      </div>

      {/* Details */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Poste:</span>
          <p className="font-medium truncate">{displayPosition}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Département:</span>
          <p className="font-medium truncate">{displayDepartment || "-"}</p>
        </div>
        <div>
          <span className="text-muted-foreground">Pays:</span>
          <div className="flex items-center gap-1">
            <MapPin className="h-3 w-3 text-primary" />
            <span className="font-medium truncate">
              {employee.current_country || employee.residence_country || "Non renseigné"}
            </span>
          </div>
        </div>
        <div>
          <span className="text-muted-foreground">Nest Points:</span>
          <div className="flex items-center gap-1">
            <Coins className="h-3 w-3 text-warning" />
            <span className="font-medium">{availablePoints}</span>
            <span className="text-muted-foreground">/{maxPoints}</span>
          </div>
        </div>
      </div>

      {/* Manager info */}
      {managerName && (
        <div className="text-sm">
          <span className="text-muted-foreground">Manager:</span>
          <span className="ml-1 font-medium">{managerName}</span>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t">
        <Button
          size="sm"
          variant="outline"
          className="flex-1"
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5 mr-1" />
          Modifier
        </Button>
        {employee.invitation_status === 'invitation_non_envoyee' && onSendInvitation && (
          <Button
            size="sm"
            variant="outline"
            onClick={onSendInvitation}
          >
            <Mail className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onShowHierarchy}
        >
          <Users className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};
